<template>
  <UForm
    :schema="schema"
    :state="state"
    class="grid grid-cols-1 gap-4 sm:grid-cols-2"
    @submit="onSubmit"
  >
    <UFormField size="lg" hint="Required" label="First Name" class="col-span-1">
      <UInput v-model="state.firstName" class="w-full" required />
    </UFormField>
    <UFormField size="lg" hint="Required" label="Last Name" class="col-span-1">
      <UInput v-model="state.lastName" class="w-full" required />
    </UFormField>
    <UFormField
      size="lg"
      hint="Required"
      label="Email"
      class="col-span-1 sm:col-span-2"
    >
      <UInput v-model="state.email" type="email" class="w-full" required />
    </UFormField>
    <UFormField
      size="lg"
      :hint="phoneValidationError || 'Required'"
      :label="`Phone ${!isPhoneValid && state.phone ? '⚠️' : ''}`"
      class="col-span-1 sm:col-span-2"
    >
      <div class="flex gap-2">
        <select
          v-model="state.phoneCountry"
          :class="`w-32 rounded-lg border px-3 py-2 focus:outline-none ${
            !isPhoneValid && state.phone
              ? 'border-red-500 focus:border-transparent focus:ring-2 focus:ring-red-400'
              : 'border-[var(--ui-border)] focus:border-transparent focus:ring-2 focus:ring-[var(--ui-primary)]'
          }`"
          class="bg-[var(--ui-bg)] text-[var(--ui-text)]"
          @change="handleCountryChange"
        >
          <option value="GB">🇬🇧 GB (+44)</option>
          <option value="US">🇺🇸 US (+1)</option>
          <option value="FR">🇫🇷 FR (+33)</option>
          <option value="DE">🇩🇪 DE (+49)</option>
          <option value="IT">🇮🇹 IT (+39)</option>
          <option value="ES">🇪🇸 ES (+34)</option>
          <option value="NL">🇳🇱 NL (+31)</option>
          <option value="BE">🇧🇪 BE (+32)</option>
          <option value="SE">🇸🇪 SE (+46)</option>
          <option value="NO">🇳🇴 NO (+47)</option>
          <option value="DK">🇩🇰 DK (+45)</option>
          <option value="CH">🇨🇭 CH (+41)</option>
          <option value="AU">🇦🇺 AU (+61)</option>
          <option value="CN">🇨🇳 CN (+86)</option>
          <option value="JP">🇯🇵 JP (+81)</option>
          <option value="IN">🇮🇳 IN (+91)</option>
          <option value="BR">🇧🇷 BR (+55)</option>
          <option value="MX">🇲🇽 MX (+52)</option>
          <option value="ZA">🇿🇦 ZA (+27)</option>
          <option value="AE">🇦🇪 AE (+971)</option>
        </select>
        <UInput
          :model-value="displayPhoneNumber"
          class="flex-1"
          required
          :placeholder="phonePlaceholder"
          type="tel"
          :class="{
            'border-red-500 focus:ring-red-400': !isPhoneValid && state.phone,
          }"
          @input="
            (e: Event) => {
              const target = e.target as HTMLInputElement;
              const input = target.value;
              if (input && state.phoneCountry) {
                const e164 = convertToE164(input);
                if (e164 !== state.phone) {
                  state.phone = e164;
                }
              }
            }
          "
        />
      </div>
    </UFormField>
    <UFormField size="lg" label="Organisation" class="col-span-1 sm:col-span-2">
      <UInput v-model="state.organisation" class="w-full" />
    </UFormField>
    <UFormField
      name="enquiry"
      size="lg"
      hint="Required"
      label="Enquiry"
      class="col-span-1 sm:col-span-2"
    >
      <USelect
        v-model="state.enquiry"
        size="lg"
        placeholder="Select an enquiry type"
        :items="enquiryItems"
        class="w-full"
      />
    </UFormField>
    <UFormField
      v-if="isContactPage"
      size="lg"
      label="Location"
      class="col-span-1 sm:col-span-2"
    >
      <USelect
        v-model="state.location"
        size="lg"
        icon="i-lucide-map-pin"
        placeholder="Select a location (optional)"
        :items="locationSelectItems"
        :loading="locationsLoading"
        class="w-full"
      />
    </UFormField>

    <div class="col-span-1 text-sm sm:col-span-2">
      <div>
        <div class="flex content-center items-center justify-between">
          <label class="block font-medium text-[--ui-text]"> Message </label>
          <span class="text-[--ui-text-muted]">Required</span>
        </div>
      </div>
      <div class="relative mt-1">
        <UTextarea
          v-model="state.message"
          placeholder="Enter your message..."
          required
          class="w-full"
        />
      </div>
    </div>

    <input
      v-model="state.fax_number"
      name="fax_number"
      type="text"
      tabindex="-1"
      autocomplete="off"
      aria-hidden="true"
      class="absolute -left-[9999px]"
    />

    <div class="col-span-1 sm:col-span-2">
      <USwitch
        v-model="state.privacy"
        required
        label="By selecting this, you agree to our privacy policy."
      />
    </div>

    <UButton
      type="submit"
      loading-auto
      :disabled="!state.privacy"
      class="col-span-1 w-full justify-center text-center sm:col-span-2"
      @click="
        // @ts-ignore - gtag is a valid method but type is not properly defined
        proxy.gtag('event', 'enquiry_form_submitted');
        $clientPosthog?.capture('enquiry_form_submitted');
      "
    >
      Send Message
    </UButton>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod';
import type { FormSubmitEvent } from '#ui/types';
import {
  AsYouType,
  getExampleNumber,
  isValidPhoneNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import type { CountryCode } from 'libphonenumber-js';
import type { ISbStoryData } from '@storyblok/vue';
import type { LocationStoryblok } from '~/types/component-types-sb';

const { proxy } = useScriptGoogleAnalytics();
const { fetchLocations } = useStoryblokClient();
const route = useRoute();

const isContactPage = computed(() => {
  const path = route.path.replace(/\/$/, '') || '/';
  return path === '/contact';
});
const props = defineProps({
  blok: {
    type: Object,
    default: () => ({}),
  },
  form: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: '',
  },
});
const emits = defineEmits(['submitted']);

const enquiryOptions = ['General', 'Sales', 'Support', 'Press'] as const;
const enquiryItems = [...enquiryOptions];

const schema = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email('Invalid email'),
    phoneCountry: z.string(),
    phone: z.string().min(1, 'Phone number is required'),
    organisation: z.string().optional(),
    location: z.preprocess(
      (v) => (v === '' ? undefined : v),
      z.string().optional()
    ),
    message: z.string(),
    enquiry: z.enum(enquiryOptions),
    fax_number: z.string().optional(),
    privacy: z.boolean(),
  })
  .refine(
    (data) => {
      if (!data.phone || !data.phoneCountry) return true;
      try {
        return isValidPhoneNumber(data.phone, data.phoneCountry as CountryCode);
      } catch {
        return false;
      }
    },
    {
      message: 'Please enter a valid phone number for the selected country',
      path: ['phone'],
    }
  );

type Schema = z.output<typeof schema>;

const state = reactive<Partial<Schema>>({
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  phoneCountry: 'GB',
  phone: undefined,
  message: undefined,
  organisation: undefined,
  location: undefined,
  enquiry: undefined,
  fax_number: undefined,
  privacy: false,
});

const phonePlaceholder = computed(() => {
  if (!state.phoneCountry) return 'Phone number';
  try {
    const example = getExampleNumber(
      state.phoneCountry as CountryCode,
      examples
    );
    return example?.formatNational() || 'Phone number';
  } catch {
    return 'Phone number';
  }
});

const displayPhoneNumber = computed(() => {
  if (!state.phone || !state.phoneCountry) return '';
  try {
    const asYouType = new AsYouType(state.phoneCountry as CountryCode);
    asYouType.input(state.phone);
    const formatted = asYouType.getNumber();
    if (formatted) {
      return formatted.formatNational();
    }
    return asYouType.getNationalNumber() || state.phone;
  } catch {
    return state.phone;
  }
});

const convertToE164 = (nationalInput: string): string => {
  if (!state.phoneCountry || !nationalInput) return nationalInput;
  try {
    const asYouType = new AsYouType(state.phoneCountry as CountryCode);
    asYouType.input(nationalInput);
    const e164 = asYouType.getNumber()?.number || '';
    return e164;
  } catch {
    return nationalInput;
  }
};

const handleCountryChange = () => {
  if (state.phone) {
    const asYouType = new AsYouType(state.phoneCountry as CountryCode);
    asYouType.input(state.phone);
    const e164 = asYouType.getNumber()?.number || '';
    if (e164 && e164 !== state.phone) {
      state.phone = e164;
    }
  }
};

const phoneValidationError = computed(() => {
  if (!state.phone || !state.phoneCountry) return '';
  try {
    const isValid = isValidPhoneNumber(
      state.phone,
      state.phoneCountry as CountryCode
    );
    if (!isValid) {
      return `Please enter a valid phone number for ${state.phoneCountry}`;
    }
    return '';
  } catch {
    return 'Invalid phone number format';
  }
});

const isPhoneValid = computed(() => {
  if (!state.phone || !state.phoneCountry) return true;
  try {
    return isValidPhoneNumber(state.phone, state.phoneCountry as CountryCode);
  } catch {
    return false;
  }
});

const locationStories = ref<ISbStoryData<LocationStoryblok>[]>([]);
const locationsLoading = ref(false);

// Locations that should not be selectable on the contact form
const excludedLocations = ['hazlewood'];

const locationSelectItems = computed(() => {
  const items = locationStories.value
    .map((story) => {
      const headline = story.content?.headline?.trim() || story.name || '';
      if (!headline) return null;
      if (
        excludedLocations.some((excluded) =>
          headline.toLowerCase().includes(excluded)
        )
      ) {
        return null;
      }
      const town = story.content?.town?.trim();
      return {
        label: town ? `${headline} (${town})` : headline,
        value: headline,
      };
    })
    .filter((item): item is { label: string; value: string } => item !== null)
    .sort((a, b) => a.label.localeCompare(b.label));

  // No item with value: '' — Radix Select reserves empty string for clear/placeholder.
  return items;
});

async function loadLocationsForContactPage() {
  if (!isContactPage.value || locationStories.value.length > 0) {
    return;
  }
  locationsLoading.value = true;
  try {
    locationStories.value =
      (await fetchLocations()) as unknown as ISbStoryData<LocationStoryblok>[];
  } catch (e) {
    console.error('Failed to load locations for enquiry form:', e);
  } finally {
    locationsLoading.value = false;
  }
}

watch(isContactPage, loadLocationsForContactPage, { immediate: true });

onMounted(() => {
  if (!state.phoneCountry) {
    state.phoneCountry = 'GB';
  }
});

const { formatPhone } = useFormatPhone();
const toast = useToast();

const CONTACT_FORM_ID = 'kx7yakw72k60zcaek655wxb18n8gd2n7';

async function onSubmit(event: FormSubmitEvent<Schema>) {
  const submissionData = {
    first_name: event.data.firstName,
    last_name: event.data.lastName,
    email: event.data.email,
    phone: formatPhone(event.data.phone),
    company_name: event.data.organisation ?? '',
    enquiry: event.data.enquiry,
    location: event.data.location?.trim() ?? '',
    message: event.data.message || '',
    fax_number: event.data.fax_number ?? '',
  };

  // $fetch, not useFetch: this is a one-off request from an event handler,
  // and it must throw so a rejected submission is not reported as success
  try {
    await $fetch(`/api/forms/${CONTACT_FORM_ID}/submit`, {
      method: 'POST',
      body: submissionData,
    });
  } catch (error) {
    console.error('Form submission failed:', error);
    toast.add({
      title: 'Error',
      icon: 'i-lucide-circle-alert',
      description:
        'Your message could not be sent. Please try again or contact us directly.',
      color: 'error',
    });
    return;
  }

  state.firstName = undefined;
  state.lastName = undefined;
  state.email = undefined;
  state.phoneCountry = 'GB';
  state.phone = undefined;
  state.message = undefined;
  state.organisation = undefined;
  state.enquiry = undefined;
  state.location = undefined;
  state.fax_number = undefined;
  state.privacy = false;

  emits('submitted', true);

  toast.add({
    title: 'Success',
    icon: 'i-lucide-circle-check',
    description: 'Your message has been sent.',
    color: 'success',
  });

  if (props.form.enable_redirect) {
    if (props.form.redirect_link.linktype === 'story') {
      navigateTo(props.form.redirect_link.cached_url);
    } else {
      navigateTo(props.form.redirect_link.url);
    }
  }
}
</script>
